import { useEffect, useState, type ReactNode } from 'react';
import { UserIdentity } from '@backstage/core-components';
import {
  ApiRef,
  IdentityApi,
  ProfileInfo,
  SignInPageProps,
  configApiRef,
  discoveryApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
  makeStyles,
} from '@material-ui/core';
import AccountCircleOutlinedIcon from '@material-ui/icons/AccountCircleOutlined';
import { oidcAuthApiRef } from '../../modules/auth';
import { isFeatureFlagEnabled } from '../../modules/featureFlags';
import { boschBlue, boschGray, boschRed, boschWhite } from '../../modules/theme/boschColors';
import {
  DevImpersonationCard,
  type DevUser,
} from './DevImpersonationCard';

type SupportedAuthApi = {
  getBackstageIdentity: (opts?: {
    instantPopup?: boolean;
    optional?: boolean;
  }) => Promise<
    | {
        identity: {
          type: 'user';
          userEntityRef: string;
          ownershipEntityRefs: string[];
        };
      }
    | undefined
  >;
  getProfile: () => Promise<ProfileInfo | undefined>;
};

const usePageStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    isolation: 'isolate',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
    overflow: 'hidden',
    backgroundColor: boschWhite,
    backgroundImage: [
      `radial-gradient(ellipse 90% 70% at 100% 0%, ${boschBlue[90]} 0%, transparent 55%)`,
      `radial-gradient(ellipse 60% 50% at 0% 100%, ${boschBlue[95]} 0%, transparent 50%)`,
      `linear-gradient(160deg, ${boschBlue[95]} 0%, ${boschGray[95]} 42%, ${boschWhite} 78%)`,
    ].join(', '),
  },
  backgroundDecor: {
    position: 'absolute',
    inset: 0,
    zIndex: 0,
    pointerEvents: 'none',
    overflow: 'hidden',
  },
  backgroundGlow: {
    position: 'absolute',
    top: '-18%',
    right: '-8%',
    width: 'min(52vw, 520px)',
    height: 'min(52vw, 520px)',
    borderRadius: '50%',
    background: `radial-gradient(circle, ${boschBlue[80]}55 0%, transparent 68%)`,
  },
  backgroundGrid: {
    position: 'absolute',
    inset: 0,
    opacity: 0.35,
    backgroundImage: [
      `linear-gradient(${boschBlue[90]}55 1px, transparent 1px)`,
      `linear-gradient(90deg, ${boschBlue[90]}55 1px, transparent 1px)`,
    ].join(', '),
    backgroundSize: '48px 48px',
    WebkitMaskImage:
      'radial-gradient(ellipse 80% 70% at 50% 40%, black 15%, transparent 75%)',
    maskImage:
      'radial-gradient(ellipse 80% 70% at 50% 40%, black 15%, transparent 75%)',
  },
  backgroundWave: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: 'auto',
    display: 'block',
    color: boschBlue[85],
    opacity: 0.55,
  },
  shell: {
    position: 'relative',
    zIndex: 1,
    width: '100%',
    maxWidth: 920,
  },
  brand: {
    textAlign: 'center',
    marginBottom: theme.spacing(5),
    paddingBottom: theme.spacing(1),
  },
  titleAccent: {
    width: 56,
    height: 4,
    borderRadius: 2,
    backgroundColor: boschBlue[50],
    margin: `0 auto ${theme.spacing(2.5)}px`,
  },
  eyebrow: {
    display: 'block',
    color: boschBlue[50],
    fontWeight: 600,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    fontSize: '0.75rem',
    marginBottom: theme.spacing(1),
  },
  title: {
    fontWeight: 700,
    color: boschBlue[20],
    letterSpacing: '-0.03em',
    lineHeight: 1.12,
    [theme.breakpoints.up('sm')]: {
      fontSize: '2.5rem',
    },
  },
  subtitle: {
    color: boschGray[40],
    marginTop: theme.spacing(1.5),
    maxWidth: 460,
    marginLeft: 'auto',
    marginRight: 'auto',
    lineHeight: 1.6,
    fontSize: '1rem',
  },
  cardsSection: {
    marginTop: theme.spacing(1),
  },
  footer: {
    marginTop: theme.spacing(4),
    textAlign: 'center',
    color: boschGray[50],
    fontSize: '0.8rem',
  },
}));

const useCardStyles = makeStyles(theme => ({
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 12,
    border: `1px solid ${boschGray[90]}`,
    borderTop: `3px solid ${boschBlue[50]}`,
    backgroundColor: boschWhite,
    boxShadow: `0 8px 28px ${boschBlue[20]}14`,
    transition: 'box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease',
    '&:hover': {
      borderTopColor: boschBlue[40],
      boxShadow: `0 14px 36px ${boschBlue[20]}22`,
      transform: 'translateY(-2px)',
    },
  },
  cardContent: {
    flexGrow: 1,
    paddingBottom: theme.spacing(1),
  },
  iconWrap: {
    width: 44,
    height: 44,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    backgroundColor: boschBlue[95],
    color: boschBlue[50],
  },
  cardTitle: {
    fontWeight: 600,
    color: boschBlue[20],
    marginBottom: theme.spacing(0.75),
  },
  message: {
    color: boschGray[40],
    lineHeight: 1.55,
  },
  actions: {
    padding: theme.spacing(2, 3, 3),
  },
  submitButton: {
    width: '100%',
    paddingTop: theme.spacing(1.25),
    paddingBottom: theme.spacing(1.25),
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: 8,
    backgroundColor: boschBlue[50],
    color: boschWhite,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: boschBlue[40],
      boxShadow: `0 4px 14px ${boschBlue[50]}55`,
    },
    '&.Mui-disabled': {
      backgroundColor: boschGray[85],
      color: boschGray[60],
    },
  },
  errorBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 8,
    backgroundColor: boschRed[95],
    border: `1px solid ${boschRed[85]}`,
  },
  errorText: {
    color: boschRed[40],
    fontSize: '0.875rem',
    lineHeight: 1.45,
  },
}));

/**
 * Why gate before optional session restore / popup: yarn start serves
 * :3000 (and even :7007 /healthcheck) while plugins are still
 * initializing. Listening on :7007 happens ~10s before the OIDC
 * provider is registered; calling getBackstageIdentity in that window
 * looks like a stuck login. Bounded exponential backoff (1s→8s, cap
 * 60s) — not an infinite loop and not a blind sleep.
 *
 * Ready signal: /api/auth/oidc/refresh?optional=1 returning any HTTP
 * response other than 404/502/503. 400/401 = auth is up, no session.
 * /healthcheck alone is insufficient (HTTP listens before auth init).
 */
const waitForBackendHealth = async (
  backendBaseUrl: string,
  opts: { maxMs?: number; signal?: { cancelled: boolean } } = {},
): Promise<boolean> => {
  const maxMs = opts.maxMs ?? 60_000;
  const started = Date.now();
  let delayMs = 1_000;
  const base = backendBaseUrl.replace(/\/$/, '');
  while (Date.now() - started < maxMs) {
    if (opts.signal?.cancelled) return false;
    try {
      const health = await fetch(`${base}/healthcheck`, {
        method: 'GET',
        credentials: 'omit',
      });
      if (health.ok) {
        const refresh = await fetch(`${base}/api/auth/oidc/refresh?optional=1`, {
          method: 'GET',
          credentials: 'include',
        });
        // 404/502/503 → auth (or proxy) not ready yet
        if (![404, 502, 503].includes(refresh.status)) {
          return true;
        }
      }
    } catch {
      // backend not listening yet
    }
    await new Promise<void>(resolve => {
      setTimeout(resolve, delayMs);
    });
    delayMs = Math.min(delayMs * 2, 8_000);
  }
  return false;
};

const authStageLog = (enabled: boolean, stage: string, detail?: string) => {
  if (!enabled) return;
  // Dev-only diagnostics for post-restart OIDC hangs — no tokens/codes.
  // eslint-disable-next-line no-console
  console.info(`[auth] ${stage}${detail ? `: ${detail}` : ''}`);
};

type ProviderSignInCardProps = {
  title: string;
  message: string;
  icon: ReactNode;
  apiRef: ApiRef<SupportedAuthApi>;
  onSignInSuccess: (identityApi: IdentityApi) => void;
  backendBaseUrl: string;
  authDebug: boolean;
};

const ProviderSignInCard = ({
  title,
  message,
  icon,
  apiRef,
  onSignInSuccess,
  backendBaseUrl,
  authDebug,
}: ProviderSignInCardProps) => {
  const classes = useCardStyles();
  const authApi = useApi(apiRef);
  const [error, setError] = useState<Error | undefined>();
  const [busy, setBusy] = useState(false);

  const completeSignIn = async (identityResponse: {
    identity: {
      type: 'user';
      userEntityRef: string;
      ownershipEntityRefs: string[];
    };
  }) => {
    authStageLog(authDebug, 'authenticated', identityResponse.identity.userEntityRef);
    const profile = await authApi.getProfile();
    onSignInSuccess(
      UserIdentity.create({
        identity: identityResponse.identity,
        authApi: authApi as any,
        profile: profile ?? {},
      }),
    );
  };

  useEffect(() => {
    const signal = { cancelled: false };
    (async () => {
      authStageLog(authDebug, 'initializing-authentication');
      const ready = await waitForBackendHealth(backendBaseUrl, { signal });
      if (signal.cancelled) return;
      if (!ready) {
        authStageLog(authDebug, 'authentication-error', 'backend healthcheck timed out');
        return;
      }
      authStageLog(authDebug, 'restoring-session');
      try {
        const existing = await authApi.getBackstageIdentity({ optional: true });
        if (!signal.cancelled && existing) {
          await completeSignIn(existing);
        } else {
          authStageLog(authDebug, 'ready-for-login');
        }
      } catch {
        // silent: user just hasn't signed in yet, ignore
        authStageLog(authDebug, 'ready-for-login', 'no existing session');
      }
    })();
    return () => {
      signal.cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authApi, backendBaseUrl, authDebug]);

  const onClick = async () => {
    setError(undefined);
    setBusy(true);
    authStageLog(authDebug, 'redirecting-to-login');
    try {
      const ready = await waitForBackendHealth(backendBaseUrl, { maxMs: 30_000 });
      if (!ready) {
        throw new Error(
          'Backend is not ready yet (healthcheck). Wait a few seconds and try again.',
        );
      }
      const res = await authApi.getBackstageIdentity({ instantPopup: true });
      if (!res) {
        throw new Error(`The ${title} provider is not configured for sign-in`);
      }
      authStageLog(authDebug, 'processing-callback');
      await completeSignIn(res);
    } catch (err) {
      authStageLog(
        authDebug,
        'authentication-error',
        err instanceof Error ? err.message : String(err),
      );
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(false);
    }
  };

  const buttonLabel = (() => {
    if (busy) return 'Signing in…';
    return 'Sign in';
  })();

  return (
    <Card className={classes.card} elevation={0}>
      <CardContent className={classes.cardContent}>
        <div className={classes.iconWrap} aria-hidden>
          {icon}
        </div>
        <Typography variant="h6" component="h2" className={classes.cardTitle}>
          {title}
        </Typography>
        <Typography variant="body2" className={classes.message}>
          {message}
        </Typography>
        {error && (
          <div className={classes.errorBox} role="alert">
            <Typography className={classes.errorText}>{error.message}</Typography>
          </div>
        )}
      </CardContent>
      <CardActions className={classes.actions}>
        <Button
          color="primary"
          variant="contained"
          disabled={busy}
          onClick={onClick}
          className={classes.submitButton}
          startIcon={busy ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          {buttonLabel}
        </Button>
      </CardActions>
    </Card>
  );
};

/**
 * Custom Backstage SignInPage that renders the OIDC provider card in a
 * centered, responsive layout. We deliberately do NOT wrap the default
 * `<SignInPage>` here because it doesn't accept extra children/siblings
 * inside its provider grid.
 *
 * Optional sibling: Dev impersonation (ManagedUsers personas) when
 * auth.environment=development AND digitalFactory.features.devImpersonation
 * AND auth.providers.devLogin.users — see ADR 0004. Never shown in Azure.
 */
export const SignInPage = (props: SignInPageProps) => {
  const classes = usePageStyles();
  const configApi = useApi(configApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const title = configApi.getOptionalString('app.title') ?? 'Backstage';

  const authEnvironment =
    configApi.getOptionalString('auth.environment') ?? 'development';
  const backendBaseUrl =
    configApi.getOptionalString('backend.baseUrl') ?? 'http://localhost:7007';
  const authDebug = authEnvironment === 'development';
  const showDevImpersonation =
    authEnvironment === 'development' &&
    isFeatureFlagEnabled(configApi, 'devImpersonation') &&
    Boolean(configApi.getOptionalConfig('auth.providers.devLogin'));

  const devUsers: DevUser[] =
    configApi
      .getOptionalConfigArray('auth.providers.devLogin.users')
      ?.map(c => ({
        name: c.getString('name'),
        displayName: c.getOptionalString('displayName'),
      })) ?? [];

  return (
    <Box className={classes.root} component="main">
      <div className={classes.backgroundDecor} aria-hidden>
        <div className={classes.backgroundGlow} />
        <div className={classes.backgroundGrid} />
        <svg
          className={classes.backgroundWave}
          viewBox="0 0 1440 160"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill="currentColor"
            d="M0,96 C240,128 480,64 720,80 C960,96 1200,144 1440,112 L1440,160 L0,160 Z"
          />
          <path
            fill="currentColor"
            fillOpacity="0.45"
            d="M0,112 C320,80 640,128 960,104 C1120,92 1280,120 1440,104 L1440,160 L0,160 Z"
          />
        </svg>
      </div>
      <div className={classes.shell}>
        <header className={classes.brand}>
          <div className={classes.titleAccent} aria-hidden />
          <Typography component="p" className={classes.eyebrow}>
            Sign in
          </Typography>
          <Typography variant="h3" component="h1" className={classes.title}>
            {title}
          </Typography>
          <Typography variant="body1" className={classes.subtitle}>
            Choose how you want to authenticate. Your session is managed
            securely by the configured identity provider.
          </Typography>
        </header>

        <Grid
          container
          spacing={3}
          justifyContent="center"
          className={classes.cardsSection}
        >
          <Grid item xs={12} sm={6} md={showDevImpersonation ? 5 : 6}>
            <ProviderSignInCard
              title="Microsoft (OIDC)"
              message="Sign in using your corporate Microsoft account"
              icon={<AccountCircleOutlinedIcon />}
              apiRef={oidcAuthApiRef}
              onSignInSuccess={props.onSignInSuccess}
              backendBaseUrl={backendBaseUrl}
              authDebug={authDebug}
            />
          </Grid>
          {showDevImpersonation && devUsers.length > 0 && (
            <Grid item xs={12} sm={6} md={5}>
              <DevImpersonationCard
                users={devUsers}
                discoveryApi={discoveryApi}
                onSignInSuccess={props.onSignInSuccess}
              />
            </Grid>
          )}
        </Grid>

        <Typography className={classes.footer}>
          © Robert Bosch GmbH · Bosch Digital Factory
        </Typography>
      </div>
    </Box>
  );
};
